/* eslint-disable max-len */
import { Form } from 'antd';
import { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import { ILeiData, ILeiSubory } from 'types/leiTypes';

import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputNumberComponent from 'components/atoms/InputNumberComponent';
import InputTextArea from 'components/atoms/InputTextArea';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import UploadComponent, { IFileList } from 'components/atoms/UploadComponent';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import { IcisStaty } from 'services/cisStaty';
import fileDownload from 'services/fileDownload';
import checkLeiApi from 'services/lei/checkLEI';
import prepareFile from 'services/lei/fileDownload';

import { EAnoNe } from 'enums/anoNe';
import { EPeriodType, ETypDatMatky, ETypMatky } from 'enums/lei/leiDataEnums';
import { EMaterskaSpol } from 'enums/lei/materskaSpol';

import anoNe from 'data/codebooks/anoNeSelect';
import { leiKonsolidaceSouborySelect } from 'data/codebooks/lei/leiKonsolidaceSouborySelect';
import { leiKonsolidaceTypSelectAlt } from 'data/codebooks/lei/leiKonsolidaceTypSelect';
import { leiMaMaterskouSpolPrimaSelect } from 'data/codebooks/lei/leiMaMaterskouSpolSelect';
import { leiMatNelzeDuvodSelect } from 'data/codebooks/lei/leiMatNelzeDuvodSelect';
import { leiMatNemaDuvodSelect } from 'data/codebooks/lei/leiMatNemaDuvodSelect';
import { leiRejstrikyPermanent, leiRejstrikySelect } from 'data/codebooks/lei/leiRejstrikySelect';

import { _getKeyValue_ } from 'utils/getObjectKeyValue';

interface IPrimaMatka {
    primaMatkaData?: ILeiData['matky'];
    internationalMatkaData?: ILeiData['matky'];
    // getCountryText: (country: string) => string | undefined;
    // getRegionText: (country: string, region: string) => string | undefined;
    setFormData?: React.Dispatch<React.SetStateAction<ILeiData | undefined>>;
    isEdit?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    upravaForm?: any;
    cisStaty: IcisStaty[];
    setUploading?: React.Dispatch<React.SetStateAction<boolean>>;
}

const PrimaMatka: React.FC<IPrimaMatka> = ({
    primaMatkaData,
    internationalMatkaData,
    //  getCountryText,
    //  getRegionText,
    isEdit,
    setFormData,
    upravaForm,
    cisStaty,
    setUploading
}) => {
    const { lei } = useContext(Context);
    const [leiCheckLoading, setLeiCheckLoading] = useState<boolean>(false);
    const [leiCheckError, setLeiCheckError] = useState<boolean>(false);

    /* const hqLangValues =
        primaMatkaData &&
        primaMatkaData[0].matkabezlei?.adresy &&
        primaMatkaData[0].matkabezlei.adresy.filter(e => e.typadresy === ETypAdresy.HEADQUARTERSADDRES);
*/
    const primaMatkaDates = {
        accounting: primaMatkaData?.length
            ? primaMatkaData[0].informacekmatce &&
              primaMatkaData[0].informacekmatce.relationshipperiod?.filter(
                  e => e.periodtype === EPeriodType.ACCOUNTING_PERIOD
              )[0]
            : undefined,
        document: primaMatkaData?.length
            ? primaMatkaData[0].informacekmatce &&
              primaMatkaData[0].informacekmatce.relationshipperiod?.filter(
                  e => e.periodtype === EPeriodType.DOCUMENT_FILING_PERIOD
              )[0]
            : undefined,
        relationship:
            primaMatkaData && primaMatkaData.length > 0
                ? primaMatkaData[0].informacekmatce &&
                  primaMatkaData[0].informacekmatce.relationshipperiod?.filter(
                      e => e.periodtype === EPeriodType.RELATIONSHIP_PERIOD
                  )[0]
                : undefined
    };

    const setForceRerender = useState<boolean>(false)[1];
    const rerender = () => {
        setForceRerender(val => !val);
    };

    const checkLei = (leiCislo: string) => {
        setLeiCheckLoading(true);
        checkLeiApi(leiCislo)
            .then(data =>
                upravaForm?.setFieldsValue({
                    pleinazev: data.spolecnost
                })
            )
            .catch(() => setLeiCheckError(true))
            .finally(() => setLeiCheckLoading(false));
    };

    useEffect(() => {
        upravaForm?.setFieldsValue({
            pduvod: primaMatkaData?.length ? primaMatkaData[0].duvod : undefined,
            pmalei: primaMatkaData?.length
                ? primaMatkaData[0].matkaslei
                    ? 'ano'
                    : primaMatkaData[0].matkabezlei && 'ne'
                : undefined,
            pvalidationdocuments: primaMatkaData?.length
                ? primaMatkaData[0].informacekmatce?.validationdocuments
                : undefined,
            prelationship: dayjs(primaMatkaDates.relationship?.startdate)
        });
        primaMatkaData?.length ? primaMatkaData[0].matkaslei && checkLei(primaMatkaData[0].matkaslei) : undefined;
        setTimeout(() => rerender(), 0); // rerender po nastaveni default values (inak sa niektore nezobrazia)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [prilozeneSoubory, setPrilozeneSoubory] = useState<ILeiSubory[]>(
        primaMatkaData?.length
            ? primaMatkaData[0].informacekmatce?.soubory
                ? primaMatkaData[0].informacekmatce.soubory
                : []
            : []
    );

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
        isEdit === false &&
            prilozeneSoubory.length !==
                (primaMatkaData?.length
                    ? primaMatkaData[0].informacekmatce?.soubory
                        ? primaMatkaData[0].informacekmatce.soubory
                        : []
                    : []
                ).length &&
            setFormData &&
            setFormData(pv => {
                return {
                    ...pv,
                    matky: [
                        ...(pv && pv.matky && pv.matky.length > 0
                            ? pv.matky.map(e =>
                                  e.typmatky === ETypMatky.IS_DIRECTLY_CONSOLIDATED_BY ||
                                  (e.typmatky === ETypMatky.IS_ULTIMATELY_CONSOLIDATED_BY &&
                                      e.stav === EMaterskaSpol.ANO_SAME) ||
                                  e.typmatky === ETypMatky.IS_INTERNATIONAL_BRANCH_OF
                                      ? {
                                            ...e,
                                            informacekmatce: {
                                                ...e.informacekmatce,
                                                soubory: prilozeneSoubory
                                            }
                                        }
                                      : e
                              )
                            : [])
                    ]
                };
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prilozeneSoubory]);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [fileList, setFileList] = useState<IFileList[] | undefined>(
        (primaMatkaData?.length && primaMatkaData[0].informacekmatce?.soubory
            ? primaMatkaData[0].informacekmatce.soubory
            : []
        ).map((e, i) => {
            return {
                uid: String(i),
                name: e.orignazev ? e.orignazev : 'bez nazvu',
                fileName: e.tempnazev ? e.tempnazev : 'a',
                type: e.orignazev ? e.orignazev.split('.').slice(-1)[0] : '',
                size: 0,
                status: 'done'
            };
        })
    );

    const uploadProps = {
        showDownloadIcon: true,
        onChange: (data: UploadChangeParam) => {
            if (data.file.status === 'uploading' && setUploading) setUploading(true);
            if (data.file.status !== 'uploading' && setUploading) setUploading(false);
            if (data.fileList.length) {
                if (data.file.response) {
                    upravaForm?.validateFields(['psoubor']);
                    setPrilozeneSoubory(
                        data.fileList
                            .filter(f => f.status && f.status !== 'error')
                            .map(e => {
                                return {
                                    orignazev: e.name.normalize(),
                                    tempnazev: e.response ? e.response.tempnazev : e.fileName
                                };
                            })
                    );
                }
                setFileList(
                    data.fileList
                        .filter(f => f.status && f.status !== 'error')
                        .map(e => {
                            return {
                                uid: e.uid,
                                name: e.name.normalize(),
                                fileName: e.fileName ? e.fileName : e.response ? e.response.tempnazev : '',
                                type: e.type!,
                                size: e.size!,
                                status: e.status ? e.status : 'error'
                            };
                        })
                );
            }
        },
        onRemove: (file: UploadFile) => {
            if (fileList?.length) {
                const oldState = [...fileList];
                const newState = oldState.filter(e => e.uid !== file.uid);
                setFileList(newState);
                const newSoubory = [...prilozeneSoubory].filter(e => e.tempnazev !== file.fileName);
                setPrilozeneSoubory(newSoubory);
            }
        },
        onDownload: (file: UploadFile) =>
            prepareFile(file.fileName ? file.fileName : '', lei?.idVarsymZadosti?.varsym || '').then(resp => {
                fileDownload(resp.redirect, file.name ? file.name : 'file');
            }),
        fileList: fileList,
        buttonDisabled: fileList ? fileList.length > 4 : false
    };

    return (
        <FormWrapper outForm={upravaForm} onFinish={() => {}} layoutPosition="horizontal">
            <>
                {!internationalMatkaData?.length && !!primaMatkaData?.length && (
                    <SelectComponent
                        notRequired={true}
                        disabled={true}
                        isFormItem={true}
                        name="pstav"
                        defaultValue={primaMatkaData && primaMatkaData[0].stav}
                        labelParagraph={
                            internationalMatkaData?.length ? 'LEI_DETAIL_MAT_SELECT_BRANCH' : 'LEI_DETAIL_MAT_SELECT'
                        }
                        data={leiMaMaterskouSpolPrimaSelect}
                    />
                )}
                {!!primaMatkaData?.length && primaMatkaData[0].stav === EMaterskaSpol.ANO && (
                    <>
                        {!internationalMatkaData?.length && (
                            <SelectComponent
                                notRequired={true}
                                disabled={true}
                                isFormItem={true}
                                name="pmalei"
                                defaultValue={primaMatkaData && primaMatkaData[0].matkaslei ? 'ano' : 'ne'}
                                labelParagraph={
                                    internationalMatkaData?.length ? 'LEI_DETAIL_MAMATKA_BRANCH' : 'LEI_DETAIL_MAMATKA'
                                }
                                data={anoNe}
                            />
                        )}

                        {(primaMatkaData && primaMatkaData[0].matkaslei ? 'ano' : 'ne') === EAnoNe.ANO && (
                            <LoadingWrapper loading={leiCheckLoading} loadingText="NACITANI_DAT">
                                <InputComponent
                                    notRequired={true}
                                    disabled={true}
                                    label={
                                        internationalMatkaData?.length
                                            ? 'LEI_DETAIL_MATER_SPOLOCNOSTI_BRANCH'
                                            : 'LEI_DETAIL_MATER_SPOLOCNOSTI'
                                    }
                                    name="pmatkaslei"
                                    initialValue={primaMatkaData && primaMatkaData[0].matkaslei}
                                    extramessage={leiCheckError ? 'LEI_CHECK_ERROR' : ''}
                                />
                                {upravaForm?.getFieldValue('pleinazev') && (
                                    <InputComponent
                                        notRequired={true}
                                        disabled={true}
                                        label={
                                            internationalMatkaData?.length
                                                ? 'LEI_DETAIL_MATER_NAZEV_BRANCH'
                                                : 'LEI_DETAIL_MATER_NAZEV'
                                        }
                                        name="pleinazev"
                                        placeholder=""
                                        initialValue={''}
                                        rules={[]}
                                    />
                                )}
                            </LoadingWrapper>
                        )}
                        {(primaMatkaData && primaMatkaData[0].matkaslei ? 'ano' : 'ne') === EAnoNe.NE && (
                            <>
                                <SelectComponent
                                    notRequired={true}
                                    disabled={true}
                                    isFormItem={true}
                                    name="pjurisdikce"
                                    defaultValue={primaMatkaData && primaMatkaData[0].matkabezlei?.jurisdikce}
                                    labelParagraph="LEI_DETAIL_JURISDICKE"
                                    data={cisStaty}
                                />
                                <SelectComponent
                                    notRequired={true}
                                    disabled={true}
                                    isFormItem={true}
                                    showOptionValue={true}
                                    name="pregistrationauthorityid"
                                    defaultValue={
                                        primaMatkaData && primaMatkaData[0].matkabezlei?.registrationauthorityid
                                    }
                                    labelParagraph="LEI_DETAIL_REJSTRIK"
                                    optionValueFilter={['RA888888']}
                                    data={[
                                        ...(primaMatkaData &&
                                        primaMatkaData[0].matkabezlei?.jurisdikce &&
                                        Object.keys(leiRejstrikySelect).includes(
                                            primaMatkaData[0].matkabezlei?.jurisdikce
                                        )
                                            ? _getKeyValue_(primaMatkaData[0].matkabezlei.jurisdikce)(
                                                  leiRejstrikySelect
                                              )
                                            : []),
                                        ...leiRejstrikyPermanent
                                    ]}
                                />
                                {primaMatkaData && primaMatkaData[0].matkabezlei?.otherregistrationauthorityid && (
                                    <InputComponent
                                        notRequired={true}
                                        disabled={true}
                                        label="LEI_REJSTRIK_INY"
                                        name="potherregistrationauthorityid"
                                        initialValue={
                                            primaMatkaData &&
                                            primaMatkaData[0].matkabezlei?.otherregistrationauthorityid
                                        }
                                    />
                                )}
                                {primaMatkaData && primaMatkaData[0].matkabezlei?.registrationauthorityentityid && (
                                    <InputComponent
                                        notRequired={true}
                                        disabled={true}
                                        label="LEI_DETAIL_IDMAT"
                                        name="pregistrationauthorityentityid"
                                        initialValue={
                                            primaMatkaData &&
                                            primaMatkaData[0].matkabezlei?.registrationauthorityentityid
                                        }
                                        rules={[]}
                                    />
                                )}
                            </>
                        )}
                        {primaMatkaData && primaMatkaData[0].matkaslei && (
                            <>
                                <DatePickerComponent
                                    notRequired={true}
                                    disabled={true}
                                    disabledDate={curr => curr > dayjs()}
                                    label="LEI_DETAIL_MATKA_DATUM"
                                    name="prelationship"
                                    initialValue={
                                        primaMatkaDates && primaMatkaDates.relationship
                                            ? dayjs(primaMatkaDates.relationship.startdate)
                                            : undefined
                                    }
                                />
                                <InputNumberComponent
                                    horizontalLayout={true}
                                    notRequired={true}
                                    disabled={true}
                                    label="LEI_DETAIL_MATKA_PODIL"
                                    name="ppodilspolecnosti"
                                    initialValue={primaMatkaData && primaMatkaData[0].informacekmatce?.podilspolecnosti}
                                />
                                <SelectComponent
                                    notRequired={true}
                                    disabled={true}
                                    isFormItem={true}
                                    name="pkonsolidace"
                                    defaultValue={primaMatkaData && primaMatkaData[0].informacekmatce?.konsolidace}
                                    labelParagraph="LEI_DETAIL_MATKA_KONSOLIDACE"
                                    data={leiKonsolidaceTypSelectAlt}
                                />
                                {isEdit === false ? (
                                    <Form.Item
                                        name="psoubor"
                                        label={<Paragraph text={'SOUBOR'} strong={true} />}
                                        rules={[
                                            {
                                                required: true,
                                                validator() {
                                                    try {
                                                        if (!fileList || fileList.length < 1)
                                                            return Promise.reject(
                                                                <TranslateText>
                                                                    NEVALIDNI_POVINNA_POLOZKA_UPLOAD
                                                                </TranslateText>
                                                            );
                                                        return Promise.resolve();
                                                    } catch {
                                                        return Promise.reject();
                                                    }
                                                }
                                            }
                                        ]}
                                    >
                                        <div
                                            style={{
                                                marginBottom: 22
                                            }}
                                        >
                                            <UploadComponent
                                                {...uploadProps}
                                                accept={'.pdf, .rtf, .docx, .doc'}
                                                buttonId="primaMatkaPrehledUploadBtn"
                                            />
                                        </div>
                                    </Form.Item>
                                ) : (
                                    fileList &&
                                    fileList.length > 0 && (
                                        <Form.Item name="psoubor" label={<Paragraph text={'SOUBOR'} strong={true} />}>
                                            <div
                                                style={{
                                                    marginBottom: 22
                                                }}
                                            >
                                                <UploadComponent
                                                    buttonId="primaMatkaPrehledUploadEdit"
                                                    {...{
                                                        showRemoveIcon: false,
                                                        onDownload: (file: UploadFile) =>
                                                            prepareFile(
                                                                file.fileName ? file.fileName : '',
                                                                lei?.idVarsymZadosti?.varsym || ''
                                                            ).then(resp => {
                                                                fileDownload(
                                                                    resp.redirect,
                                                                    file.name ? file.name : 'file'
                                                                );
                                                            }),
                                                        hideButton: true
                                                    }}
                                                    fileList={fileList}
                                                    accept={'.pdf, .rtf, .docx, .doc'}
                                                />
                                            </div>
                                        </Form.Item>
                                    )
                                )}
                                {isEdit === false && (
                                    <>
                                        <SelectComponent
                                            isFormItem={true}
                                            name="pvalidationdocuments"
                                            defaultValue={
                                                primaMatkaData && primaMatkaData[0].informacekmatce?.validationdocuments
                                            }
                                            labelParagraph="LEI_DETAIL_MATKA_KONSOLIDACE_CONFIRM"
                                            data={leiKonsolidaceSouborySelect}
                                            onChange={() => rerender()}
                                        />
                                        {upravaForm?.getFieldValue('pvalidationdocuments') ===
                                            ETypDatMatky.REGULATORY_FILING && (
                                            <InputComponent
                                                label="LEI_DETAIL_REGULATORY"
                                                name="pvalidationreference"
                                                initialValue={
                                                    primaMatkaData &&
                                                    primaMatkaData[0].informacekmatce?.validationreference
                                                }
                                            />
                                        )}
                                        {upravaForm?.getFieldValue('pvalidationdocuments') && (
                                            <>
                                                <DatePickerComponent
                                                    disabledDate={current =>
                                                        upravaForm?.getFieldValue('paccountingend') &&
                                                        current >
                                                            dayjs(upravaForm?.getFieldValue('paccountingend')).subtract(
                                                                1,
                                                                'days'
                                                            )
                                                    }
                                                    label="LEI_DETAIL_UCETNI_OBDOBY_OD"
                                                    name="paccountingstart"
                                                    initialValue={
                                                        primaMatkaDates && primaMatkaDates.accounting
                                                            ? dayjs(primaMatkaDates.accounting.startdate || undefined)
                                                            : undefined
                                                    }
                                                />
                                                <DatePickerComponent
                                                    disabledDate={current =>
                                                        upravaForm?.getFieldValue('paccountingstart') &&
                                                        current <
                                                            dayjs(upravaForm?.getFieldValue('paccountingstart')).add(
                                                                1,
                                                                'days'
                                                            )
                                                    }
                                                    label="LEI_DETAIL_UCETNI_OBDOBY_DO"
                                                    name="paccountingend"
                                                    initialValue={
                                                        primaMatkaDates && primaMatkaDates.accounting
                                                            ? primaMatkaDates.accounting.enddate &&
                                                              dayjs(primaMatkaDates.accounting.enddate)
                                                            : undefined
                                                    }
                                                    notRequired={
                                                        upravaForm.getFieldValue('pvalidationdocuments') !==
                                                            ETypDatMatky.ACCOUNTS_FILING &&
                                                        upravaForm.getFieldValue('pvalidationdocuments') !==
                                                            ETypDatMatky.REGULATORY_FILING
                                                    }
                                                />
                                            </>
                                        )}
                                        {upravaForm?.getFieldValue('pvalidationdocuments') &&
                                            upravaForm?.getFieldValue('pvalidationdocuments') !==
                                                ETypDatMatky.ACCOUNTS_FILING && (
                                                <>
                                                    <DatePickerComponent
                                                        disabledDate={current =>
                                                            upravaForm?.getFieldValue('pdocumentend') &&
                                                            current >
                                                                dayjs(
                                                                    upravaForm?.getFieldValue('pdocumentend')
                                                                ).subtract(1, 'days')
                                                        }
                                                        label="LEI_DETAIL_PLATNOSTDOKUMENTU_OD"
                                                        name="pdocumentstart"
                                                        initialValue={
                                                            primaMatkaDates && primaMatkaDates.document
                                                                ? dayjs(primaMatkaDates.document.startdate || undefined)
                                                                : undefined
                                                        }
                                                    />
                                                    <DatePickerComponent
                                                        disabledDate={current =>
                                                            upravaForm?.getFieldValue('pdocumentstart') &&
                                                            current <
                                                                dayjs(upravaForm?.getFieldValue('pdocumentstart')).add(
                                                                    1,
                                                                    'days'
                                                                )
                                                        }
                                                        label="LEI_DETAIL_PLATNOSTDOKUMENTU_DO"
                                                        name="pdocumentend"
                                                        initialValue={
                                                            primaMatkaDates && primaMatkaDates.document?.enddate
                                                                ? dayjs(primaMatkaDates.document.enddate)
                                                                : undefined
                                                        }
                                                        notRequired={true}
                                                    />
                                                </>
                                            )}
                                    </>
                                )}
                                {isEdit !== false && (
                                    <>
                                        <SelectComponent
                                            notRequired={true}
                                            disabled={true}
                                            isFormItem={true}
                                            name="pvalidationdocuments"
                                            defaultValue={
                                                primaMatkaData && primaMatkaData[0].informacekmatce?.validationdocuments
                                            }
                                            labelParagraph="LEI_DETAIL_MATKA_KONSOLIDACE_CONFIRM"
                                            data={leiKonsolidaceSouborySelect}
                                        />
                                        {primaMatkaData &&
                                            primaMatkaData[0].informacekmatce?.validationdocuments ===
                                                ETypDatMatky.REGULATORY_FILING && (
                                                <InputComponent
                                                    notRequired={true}
                                                    disabled={true}
                                                    label="LEI_DETAIL_REGULATORY"
                                                    name="pvalidationreference"
                                                    initialValue={
                                                        primaMatkaData &&
                                                        primaMatkaData[0].informacekmatce?.validationreference
                                                    }
                                                />
                                            )}
                                        {primaMatkaData && primaMatkaData[0].informacekmatce?.validationdocuments && (
                                            <InputComponent
                                                notRequired={true}
                                                disabled={true}
                                                label="LEI_DETAIL_UCETNI_OBDOBY"
                                                name="paccountingstart1"
                                                initialValue={`${
                                                    dayjs(primaMatkaDates?.accounting?.startdate).format('DD.MM.YYYY') +
                                                    ' -'
                                                } ${
                                                    primaMatkaDates?.accounting?.enddate
                                                        ? dayjs(primaMatkaDates?.accounting?.enddate).format(
                                                              'DD.MM.YYYY'
                                                          )
                                                        : ''
                                                }`}
                                            />
                                        )}
                                        {primaMatkaData &&
                                            primaMatkaData[0].informacekmatce?.validationdocuments &&
                                            primaMatkaData &&
                                            primaMatkaData[0].informacekmatce?.validationdocuments !==
                                                ETypDatMatky.ACCOUNTS_FILING && (
                                                <InputComponent
                                                    notRequired={true}
                                                    disabled={true}
                                                    label="LEI_DETAIL_PLATNOSTDOKUMENTU"
                                                    name="paccountingstart2"
                                                    initialValue={`${
                                                        dayjs(primaMatkaDates.document?.startdate).format(
                                                            'DD.MM.YYYY'
                                                        ) + ' -'
                                                    } ${
                                                        primaMatkaDates.document?.enddate
                                                            ? dayjs(primaMatkaDates.document?.enddate).format(
                                                                  'DD.MM.YYYY'
                                                              )
                                                            : ''
                                                    }`}
                                                />
                                            )}
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}
                {!!primaMatkaData?.length && primaMatkaData[0].stav === EMaterskaSpol.NE && (
                    <>
                        <SelectComponent
                            notRequired={true}
                            disabled={true}
                            isFormItem={true}
                            name="pduvod"
                            defaultValue={primaMatkaData && primaMatkaData[0].duvod}
                            labelParagraph="LEI_DETAIL_DUVOD_SELECT"
                            data={leiMatNemaDuvodSelect}
                        />
                    </>
                )}
                {!!primaMatkaData?.length && primaMatkaData[0].stav === EMaterskaSpol.NELZE_UVEST && (
                    <>
                        <SelectComponent
                            notRequired={true}
                            disabled={true}
                            isFormItem={true}
                            name="pduvod"
                            defaultValue={primaMatkaData && primaMatkaData[0].duvod}
                            labelParagraph="LEI_DETAIL_DUVOD_SELECT"
                            data={leiMatNelzeDuvodSelect}
                        />
                        <InputTextArea
                            rows={4}
                            name="pexceptionreference"
                            initialValue={primaMatkaData && primaMatkaData[0].exceptionreference}
                            label="LEI_DETAIL_NELZEUVEST_INPUT"
                            disabled={true}
                        />
                    </>
                )}
            </>
        </FormWrapper>
    );
};

export default PrimaMatka;
